import { staffOperationsControlResultSchema, staffOperationsIntentPageSchema, staffOperationsRequeueResultSchema, staffOperationsSummarySchema, } from "@lafayette/schemas";
import { ApiHttpError, ApiNetworkError, ApiResponseError } from "./api-failure";
import { createJsonTransport } from "./json-transport";
/** The API answered with an error. */
export class OperationsApiError extends ApiHttpError {
    constructor(status, code, message, requestId, retryAfter = null) {
        super(status, code, message, requestId, retryAfter);
        this.name = "OperationsApiError";
    }
}
/** The request never got an answer (no network, DNS, a closed connection) after every retry. */
export class OperationsApiNetworkError extends ApiNetworkError {
    constructor(options) {
        super("the operations API could not be reached", options);
        this.name = "OperationsApiNetworkError";
    }
}
/** The API answered 2xx with a body that does not match the contract. */
export class OperationsApiResponseError extends ApiResponseError {
    constructor() {
        super("the operations API answered with an unexpected body");
        this.name = "OperationsApiResponseError";
    }
}
/** The path of the list, with the filter as a query. A list is a comma list; an absent key is left out. */
export function staffOperationsJobsUrlPath(query = {}) {
    const params = new URLSearchParams();
    const text = (key, value) => {
        if (value === undefined)
            return;
        params.set(key, value instanceof Date ? value.toISOString() : String(value));
    };
    const list = (key, value) => {
        if (value === undefined)
            return;
        params.set(key, Array.isArray(value) ? value.join(",") : String(value));
    };
    list("state", query.state);
    list("task", query.task);
    text("subjectId", query.subjectId);
    text("updatedSince", query.updatedSince);
    text("cursor", query.cursor);
    text("limit", query.limit);
    const suffix = params.toString();
    return `/staff/operations/jobs${suffix === "" ? "" : `?${suffix}`}`;
}
export function createStaffOperationsApi(options) {
    const send = createJsonTransport(options, {
        refusedMessage: "the operations API refused the request",
        network: (cause) => new OperationsApiNetworkError({ cause }),
        response: () => new OperationsApiResponseError(),
        http: ({ status, code, message, requestId, retryAfter }) => new OperationsApiError(status, code, message, requestId, retryAfter),
    });
    const call = (method, path, schema, input = {}) => send({ method, path, schema, ...input });
    return {
        listJobs: (query, callOptions) => call("GET", staffOperationsJobsUrlPath(query), staffOperationsIntentPageSchema, {
            signal: callOptions?.signal,
        }),
        getSummary: (callOptions) => call("GET", "/staff/operations/jobs/summary", staffOperationsSummarySchema, {
            signal: callOptions?.signal,
        }),
        requeueJob: (intentId, input, commandOptions) => call("POST", `/staff/operations/jobs/${encodeURIComponent(intentId)}/requeue`, staffOperationsRequeueResultSchema, {
            body: input,
            signal: commandOptions?.signal,
            idempotencyKey: commandOptions?.idempotencyKey,
        }),
        setJobControl: (taskId, input, commandOptions) => call("PUT", `/staff/operations/job-controls/${encodeURIComponent(taskId)}`, staffOperationsControlResultSchema, {
            body: input,
            signal: commandOptions?.signal,
            idempotencyKey: commandOptions?.idempotencyKey,
        }),
    };
}
